import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  describeError,
  fetchMe,
  fetchHours,
  fetchProjects,
  fetchSyncStatus,
  fetchTeams,
  fetchUsers,
  refreshSync,
  logout,
  roleAllows,
  CSV_SHEETS,
  csvUrl,
  UNAUTHENTICATED_EVENT,
  type Me,
  type CsvSheet,
  type HoursResponse,
  type Project,
  type Team,
  type EmployeeScope,
  type Severity,
  type SyncStatus,
  type User,
} from './api.js';
import { Filters } from './components/Filters.js';
import { Login } from './components/Login.js';
import { HoursMatrix, type DrillRequest } from './components/HoursMatrix.js';
import { IssueDrilldown, type DrilldownTarget } from './components/IssueDrilldown.js';
import { RevisionIssues } from './components/RevisionIssues.js';
import { HrReconcile } from './components/HrReconcile.js';
import { Reminders } from './components/Reminders.js';
import { RevisionPeople } from './components/RevisionPeople.js';
import { formatAgo, formatHoursTotal, formatPeriod } from './format.js';
import { defaultPeriod, type Period } from './periods.js';

/**
 * Вкладки верхнего уровня. Ревизия вынесена с главного экрана намеренно: там
 * тысячи строк и другой вопрос — не «сколько часов потрачено», а «где данные
 * в Jira разошлись с реальностью». Смешивать их значит испортить оба экрана.
 */
const TABS = ['hours', 'reconcile', 'reminders', 'revision-people', 'revision-issues'] as const;
type Tab = (typeof TABS)[number];

/**
 * Вкладки, убранные с экрана по просьбе заказчика (2026-10-21): «Участники
 * Jira» и «Ревизия задач Jira».
 *
 * Удаление **мягкое**: маршруты, запросы и сами компоненты на месте, вкладка
 * открывается по прямой ссылке `?tab=revision-issues`. Убран только вход
 * в шапке. Причина в том, что обе отвечают на вопрос о качестве данных в Jira
 * (2598 незакрытых задач, 7692 часа на них), а не о часах и деньгах — и если
 * этот вопрос вернётся, возвращать код не придётся: достаточно убрать имя
 * отсюда.
 */
/**
 * Выгрузка с вкладки «Часы Jira» убрана (замечание заказчика 2026-10-21):
 * непонятно, что такое «Матрица», период читался с лишним днём, а списки
 * команд и сотрудников в листах заказчик назвал некорректными. Блок больше
 * сбивал с толку, чем помогал.
 *
 * Скрыт, а не удалён: маршруты `/api/export/*.csv` работают, и сборка листов
 * в `packages/report` та же, что у сверки. Вернуть — поставить `true`.
 */
const HOURS_EXPORT_VISIBLE = false;

const HIDDEN_TABS = new Set<Tab>(['revision-people', 'revision-issues']);
const VISIBLE_TABS = TABS.filter((name) => !HIDDEN_TABS.has(name));

/**
 * Источник указан прямо в названии: вкладки живут на данных Jira, а «Сверка»
 * сводит их с выгрузкой PeopleForce. Без этого «Часы» и «Сверка» выглядят как
 * два взгляда на одно и то же, а числа в них разные по построению.
 */
const TAB_LABELS: Record<Tab, string> = {
  hours: 'Часы Jira',
  // Сверка — вторая: после «сколько часов» сразу идёт «сходится ли это
  // с табелем». Ревизия и участники отвечают на другие вопросы и ждут дальше.
  reconcile: 'Сверка с PeopleForce',
  // Рассылка — третья (решение 2026-09-23): порядок вкладок повторяет ход
  // месячной работы. Посмотрели часы, свели с табелем, написали тем, у кого
  // не сходится. Название о действии, а не о содержании письма: напоминание
  // о часах — сегодняшний повод, а рассылка останется рассылкой.
  reminders: 'Рассылка',
  'revision-people': 'Участники Jira',
  // Ревизия — последняя: она про качество данных в Jira, а не про часы
  // и деньги, и в ежемесячной работе открывается реже всех.
  'revision-issues': 'Ревизия задач Jira',
};

/** Роль словами: `reconciler` на экране не значит ничего. */
const ROLE_LABELS: Record<Me['role'], string> = {
  viewer: 'просмотр',
  reconciler: 'сверка',
  admin: 'администратор',
};

const ROLE_HINTS: Record<Me['role'], string> = {
  viewer: 'Смотрит все вкладки и скачивает выгрузки',
  reconciler: 'Плюс загружает табель PeopleForce и подтверждает связки',
  admin: 'Плюс запускает синхронизацию и выдаёт доступы',
};

function isTab(value: string | null): value is Tab {
  return value !== null && (TABS as readonly string[]).includes(value);
}

/** An open drill-down: whose tasks, optionally narrowed to one project. */
interface Drill {
  authorKey: string;
  /** Значение поля Team; пустая строка — «без команды». */
  team?: string;
}

interface UrlState {
  tab: Tab;
  period: Period;
  teams: string[];
  authorKeys: string[];
  employeeScope: EmployeeScope;
  drill?: Drill;
}

const EMPLOYEE_SCOPES: EmployeeScope[] = ['none', 'logging', 'active'];

function isEmployeeScope(value: string | null): value is EmployeeScope {
  return value !== null && (EMPLOYEE_SCOPES as string[]).includes(value);
}

/** Filters live in the URL so a view can be sent to someone else as a link. */
function readUrlState(): UrlState {
  const params = new URLSearchParams(window.location.search);
  const fallback = defaultPeriod();
  const drillAuthor = params.get('drillAuthor');
  const drillTeam = params.get('drillTeam');

  const tab = params.get('tab');
  const scope = params.get('employees');

  return {
    tab: isTab(tab) ? tab : 'hours',
    // По умолчанию показываем и тех, кто обычно списывает, но за период не списал:
    // именно этот ноль и хотят видеть.
    // По умолчанию — только те, у кого за период есть часы (2026-10-21).
    // Раньше умолчанием было `logging`: строка с нулём напротив человека,
    // который обычно списывает, — содержательный ответ, и его показывали.
    // Но переключатель жил в блоке «Сотрудники», а блок убран с экрана,
    // и 102 пустые строки из 117 стало нечем убрать. Разрез остался в URL
    // (`?employees=logging`), менять его можно ссылкой.
    employeeScope: isEmployeeScope(scope) ? scope : 'none',
    period: {
      from: params.get('from') ?? fallback.from,
      to: params.get('to') ?? fallback.to,
    },
    teams: params.getAll('team'),
    authorKeys: params.getAll('author'),
    // The drill-down is in the URL too: "смотри, вот на что ушли эти 40 часов"
    // is exactly the kind of thing people forward.
    ...(drillAuthor
      ? { drill: { authorKey: drillAuthor, ...(drillTeam === null ? {} : { team: drillTeam }) } }
      : {}),
  };
}

function writeUrlState(state: UrlState): void {
  const params = new URLSearchParams({ from: state.period.from, to: state.period.to });
  // Вкладка в ссылке: «посмотри на эти брошенные задачи» пересылают так же,
  // как и разрез часов.
  if (state.tab !== 'hours') params.set('tab', state.tab);
  if (state.employeeScope !== 'none') params.set('employees', state.employeeScope);
  for (const team of state.teams) params.append('team', team);
  for (const key of state.authorKeys) params.append('author', key);
  if (state.drill) {
    params.set('drillAuthor', state.drill.authorKey);
    // Пустая строка — «без команды», и в ссылке она значащая.
    if (state.drill.team !== undefined) params.set('drillTeam', state.drill.team);
  }
  window.history.replaceState(null, '', `?${params.toString()}`);
}

/**
 * Дашборд. Показывается только вошедшему: `me` приходит сверху и решает,
 * какие действия человеку вообще показывать.
 */
function Dashboard({ me, onSignOut }: { me: Me; onSignOut: () => void }) {
  const initial = readUrlState();
  const [tab, setTab] = useState<Tab>(initial.tab);
  const [period, setPeriod] = useState<Period>(initial.period);
  const [teamKeys, setTeamKeys] = useState<string[]>(initial.teams);
  const [authorKeys, setAuthorKeys] = useState<string[]>(initial.authorKeys);
  const [employeeScope, setEmployeeScope] = useState<EmployeeScope>(initial.employeeScope);

  const [projects, setProjects] = useState<Project[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [sync, setSync] = useState<SyncStatus>();

  // Bumped after a sync so the table reloads without touching the filters.
  const [dataVersion, setDataVersion] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshNote, setRefreshNote] = useState<string>();
  const [csvOpen, setCsvOpen] = useState(false);

  const [hours, setHours] = useState<HoursResponse>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [drill, setDrill] = useState<Drill | undefined>(initial.drill);

  // Фильтры ревизии свои: там вопрос не про период, а про проект и цвет.
  const [revisionProjects, setRevisionProjects] = useState<string[]>([]);
  const [severity, setSeverity] = useState<Severity>();

  // Reference data changes only after a sync, so it is loaded once.
  useEffect(() => {
    const controller = new AbortController();

    Promise.all([
      fetchProjects(controller.signal),
      fetchUsers(controller.signal),
      fetchSyncStatus(controller.signal),
    ])
      .then(([loadedProjects, loadedUsers, status]) => {
        setProjects(loadedProjects);
        setUsers(loadedUsers);
        setSync(status);
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) setError(describeError(cause));
      });

    return () => controller.abort();
  }, []);

  // Команды и часы у них считаются за выбранный период: список стоит рядом
  // с фильтром периода, и часы за всю историю читались бы как часы месяца.
  useEffect(() => {
    const controller = new AbortController();
    fetchTeams(period, controller.signal).then(setTeams, () => undefined);
    return () => controller.abort();
  }, [period, dataVersion]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(undefined);

    fetchHours(
      { ...period, teams: teamKeys, authorKeys, employees: employeeScope },
      controller.signal,
    )
      .then((response) => {
        setHours(response);
        setLoading(false);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(describeError(cause));
        setLoading(false);
      });

    return () => controller.abort();
  }, [period, teamKeys, authorKeys, employeeScope, dataVersion]);

  useEffect(() => {
    writeUrlState({
      tab,
      period,
      teams: teamKeys,
      authorKeys,
      employeeScope,
      ...(drill ? { drill } : {}),
    });
  }, [tab, period, teamKeys, authorKeys, employeeScope, drill]);

  const resetAll = useCallback(() => {
    setPeriod(defaultPeriod());
    setTeamKeys([]);
    setAuthorKeys([]);
  }, []);

  /**
   * A sync can be started elsewhere — by the hourly scheduler or from the CLI —
   * and this tab would happily offer an "обновить" button that the server then
   * refuses. Re-reading the status when the person comes back to the tab keeps
   * the button honest without polling in the background.
   */
  useEffect(() => {
    const onFocus = () => {
      fetchSyncStatus().then(
        (status) => {
          setSync(status);
          if (status.running) setRefreshing(true);
        },
        () => undefined,
      );
    };

    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const startRefresh = useCallback(() => {
    setRefreshNote(undefined);

    refreshSync().then(
      (outcome) => {
        if (outcome.status === 'too_soon') {
          setRefreshNote(
            `данные только что обновлялись, повторите через ${outcome.retryAfterSeconds} с`,
          );
          return;
        }
        // `already_running` тоже сюда: значит синхронизацию начал кто-то другой,
        // и правильная реакция — показать её ход, а не ошибку.
        setRefreshing(true);
      },
      (cause: unknown) => setRefreshNote(describeError(cause)),
    );
  }, []);

  /**
   * While a sync runs, poll the status. The dashboard cannot subscribe to
   * anything — the sync happens in another process — so short polling is the
   * honest mechanism here, and it stops as soon as the run ends.
   */
  useEffect(() => {
    if (!refreshing) return;

    let cancelled = false;
    let ticks = 0;

    const timer = setInterval(() => {
      ticks += 1;

      fetchSyncStatus().then(
        (status) => {
          if (cancelled) return;
          setSync(status);
          if (status.running) {
            // A sync that never finishes must not spin forever: after ~3 minutes
            // stop watching and let the person decide what to do.
            if (ticks > 90) {
              setRefreshing(false);
              setRefreshNote('синхронизация идёт дольше обычного — проверьте журнал прогонов');
            }
            return;
          }

          setRefreshing(false);
          const last = status.runs.find((run) => run.entity === 'worklogs');
          if (last?.status === 'failed') {
            setRefreshNote(last.error ?? 'синхронизация завершилась ошибкой');
          } else {
            setDataVersion((version) => version + 1);
          }
        },
        () => undefined,
      );
    }, 2000);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [refreshing]);

  const openDrill = useCallback(
    (request: DrillRequest) =>
      setDrill({
        authorKey: request.authorKey,
        ...(request.team === undefined ? {} : { team: request.team }),
      }),
    [],
  );
  const closeDrill = useCallback(() => setDrill(undefined), []);

  /**
   * Names come from the reference lists, so a link opened cold shows the same
   * header as a click — the drill-down itself carries only ids. Memoised because
   * the panel refetches whenever this object changes.
   */
  const drillTarget: DrilldownTarget | undefined = useMemo(() => {
    if (!drill) return undefined;
    const team = teams.find((candidate) => candidate.team === drill.team);

    return {
      authorKey: drill.authorKey,
      displayName:
        users.find((user) => user.userKey === drill.authorKey)?.displayName ?? drill.authorKey,
      ...(drill.team === undefined
        ? {}
        : {
            team: drill.team,
            teamLabel: team?.label ?? (drill.team === '' ? 'без команды' : drill.team),
          }),
      fallbackTeams: teamKeys,
    };
  }, [drill, teams, users, teamKeys]);

  const peopleShown = hours ? Object.keys(hours.totals.byAuthor).length : 0;
  const teamsShown = hours ? Object.keys(hours.totals.byTeam).length : 0;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Cost Sharing</h1>
          <p className="muted">Часы по сотрудникам и командам, сверка с табелем PeopleForce</p>
        </div>
        <div className="freshness">
          {sync?.watermark ? (
            <>
              <div className="muted small">данные обновлены</div>
              <div title={sync.watermark}>{formatAgo(sync.watermark)}</div>
            </>
          ) : (
            <div className="muted small">синхронизация ещё не выполнялась</div>
          )}
          {/* Синхронизация — нагрузка на общую корпоративную Jira, поэтому
              кнопка только у администратора. Сервер проверяет это сам;
              здесь мы просто не показываем то, чего человеку нельзя. */}
          {roleAllows(me.role, 'admin') && (
            <button
              type="button"
              className="refresh"
              onClick={startRefresh}
              disabled={refreshing || sync?.running}
            >
              {refreshing || sync?.running ? 'обновляю…' : 'обновить'}
            </button>
          )}
          {refreshNote && <div className="muted small refresh-note">{refreshNote}</div>}
          <div className="whoami muted small">
            {me.displayName ?? me.login}
            <span className="role-badge" title={ROLE_HINTS[me.role]}>
              {ROLE_LABELS[me.role]}
            </span>
            <button type="button" className="link" onClick={onSignOut}>
              выйти
            </button>
          </div>
        </div>
      </header>

      <nav className="tabs">
        {VISIBLE_TABS.map((name) => (
          <button
            key={name}
            type="button"
            className={`tab${tab === name ? ' tab-on' : ''}`}
            onClick={() => setTab(name)}
          >
            {TAB_LABELS[name]}
          </button>
        ))}
      </nav>

      {tab === 'revision-issues' && (
        <RevisionIssues
          projects={projects}
          projectIds={revisionProjects}
          onProjectsChange={setRevisionProjects}
          severity={severity}
          onSeverityChange={setSeverity}
          dataVersion={dataVersion}
        />
      )}

      {tab === 'revision-people' && <RevisionPeople dataVersion={dataVersion} />}

      {tab === 'reconcile' && <HrReconcile canEdit={roleAllows(me.role, 'reconciler')} />}

      {tab === 'reminders' && <Reminders canEdit={roleAllows(me.role, 'reconciler')} />}

      {tab === 'hours' && (
        <>
          <Filters
            period={period}
            users={users}
            teams={teams}
            selectedTeams={teamKeys}
            selectedAuthors={authorKeys}
            onPeriodChange={setPeriod}
            onTeamsChange={setTeamKeys}
            onAuthorsChange={setAuthorKeys}
            employeeScope={employeeScope}
            onEmployeeScopeChange={setEmployeeScope}
          />

          {hours && !error && (
            <section className="stats">
              <Stat label="Всего часов" value={formatHoursTotal(hours.totals.seconds)} />
              <Stat label="Сотрудников" value={String(peopleShown)} />
              <Stat label="Команд" value={String(teamsShown)} />
              <Stat label="Период" value={formatPeriod(hours.period.from, hours.period.to)} />

              {HOURS_EXPORT_VISIBLE && (
                <div className="stat export">
                  <div className="stat-label">Выгрузка</div>
                  <button type="button" className="chip" onClick={() => setCsvOpen(!csvOpen)}>
                    CSV {csvOpen ? '▴' : '▾'}
                  </button>
                  {csvOpen && (
                    <div className="export-menu">
                      {CSV_SHEETS.map((sheet: CsvSheet) => (
                        <a
                          key={sheet}
                          href={csvUrl(sheet, period)}
                          onClick={() => setCsvOpen(false)}
                        >
                          {sheet}
                        </a>
                      ))}
                      {/* Никакого «BOM» и «разделителей»: человеку важно, что файл
                    просто откроется, а не как это устроено. */}
                      <div className="muted small">
                        За выбранный период ({formatPeriod(period.from, period.to)}), по всем
                        командам и сотрудникам. «Матрица» — то же, что на экране. Открывается в
                        Excel как таблица, кириллица не ломается.
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {error && (
            <div className="error">
              <strong>Не удалось загрузить данные.</strong> {error}
              <button type="button" className="link" onClick={resetAll}>
                сбросить фильтры
              </button>
            </div>
          )}

          {loading && !hours && <div className="empty">Загрузка…</div>}

          {hours && !error && (
            <div className={loading ? 'stale' : undefined}>
              <HoursMatrix data={hours} teams={teams} onDrill={openDrill} />
            </div>
          )}

          {drillTarget && (
            <IssueDrilldown target={drillTarget} period={period} onClose={closeDrill} />
          )}
        </>
      )}

      {/* У сверки подвала нет: про источники там сказано в блоке «Как читать
          таблицу», и повторять это внизу значит писать одно и то же дважды
          на экране, где и так полтора десятка колонок. */}
      {tab !== 'reconcile' && (
        <footer className="muted small">
          {tab === 'hours'
            ? 'Часы в десятичном виде. Клик по ячейке или имени показывает задачи. Верхняя граница периода не включается. Jira используется только для чтения.'
            : 'Ревизия не зависит от периода наверху: «брошено год назад» — свойство задачи, а не отчётный срок. Jira используется только для чтения — исправлять найденное нужно в самой Jira.'}
        </footer>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
}

/**
 * Вход в приложение.
 *
 * Пока сессия не проверена, не показываем ни дашборд, ни форму: мигание формой
 * у уже вошедшего человека выглядит как «меня выкинуло».
 */
export function App() {
  const [me, setMe] = useState<Me>();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetchMe(controller.signal)
      .then((found) => {
        setMe(found ?? undefined);
        setChecked(true);
      })
      .catch(() => setChecked(true));
    return () => controller.abort();
  }, []);

  // Сессия могла кончиться в любой момент — любой запрос об этом сообщит.
  useEffect(() => {
    const expired = () => setMe(undefined);
    window.addEventListener(UNAUTHENTICATED_EVENT, expired);
    return () => window.removeEventListener(UNAUTHENTICATED_EVENT, expired);
  }, []);

  const signOut = useCallback(() => {
    // Выходим в любом случае: если запрос не дошёл, сессия истечёт сама,
    // а человек, нажавший «выйти», должен увидеть форму входа сразу.
    void logout().finally(() => setMe(undefined));
  }, []);

  if (!checked) return <div className="page empty">Проверяю сессию…</div>;
  if (!me) return <Login onSuccess={setMe} />;

  return <Dashboard me={me} onSignOut={signOut} />;
}
